import { defineStore } from "pinia";
// import { $Amplify } from "@/main";
import Amplify from "aws-amplify";
import CognitoIdentityServiceProvider from "aws-sdk/clients/cognitoidentityserviceprovider";
import { sortBy } from "lodash";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { User } from "@/models/User";
import { IUserGroup } from "@/models";

export interface CognitoAdminState {
  showCustomAttributes: boolean;
  users: User[];
  userGroups: IUserGroup[];
  organisationsList: string[];
  groups: string[];
  currentUser: string;
  currentUserGroups: string[];
  paginationToken: string;
  groupPaginationToken: string;
  provider: CognitoIdentityServiceProvider | null;
  isAuthenticating: Promise<any> | null;
}

export const useCognitoAdmin = defineStore("cognitoAdmin", {
  state: (): CognitoAdminState => ({
    showCustomAttributes: false,
    users: [],
    userGroups: [],
    organisationsList: [],
    groups: [],
    currentUser: "",
    currentUserGroups: [],
    paginationToken: "",
    groupPaginationToken: "",
    provider: null,
    isAuthenticating: null,
  }),
  getters: {
    amplify() {
      return Amplify;
    },
    request(): { UserPoolId: string } {
      return { UserPoolId: this.amplify.Auth._config.userPoolId };
    },
  },
  actions: {
    async logout() {
      return this.amplify.Auth.signOut().then(() => {
        this.provider = null;
      });
    },
    async currentCredentials() {
      return this.amplify.Auth.currentCredentials().then((r: any) => ({
        ...r,
        region: this.amplify.Auth._config.region,
      }));
    },
    async currentAuthenticatedUser() {
      return this.amplify.Auth.currentAuthenticatedUser();
    },
    async getProvider(): Promise<CognitoIdentityServiceProvider> {
      if (this.provider) {
        return Promise.resolve(this.provider);
      }
      const credentials = await this.currentCredentials();
      if (!credentials.sessionToken) {
        if (!this.isAuthenticating) {
          this.isAuthenticating = new Promise((resolve) => {
            setTimeout(() => {
              resolve(this.getProvider());
            }, 321);
          });
        }
        return this.isAuthenticating;
      } else {
        this.isAuthenticating = null;
        this.provider = new CognitoIdentityServiceProvider(credentials);
        return this.provider;
      }
    },
    async createUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminCreateUserRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminCreateUserResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminCreateUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminCreateUserRequest)
        .promise();
    },
    async getUserGroup(
      request: CognitoIdentityServiceProvider.Types.ListUsersInGroupRequest
    ): Promise<IUserGroup> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .listUsersInGroup({
          ...this.request,
          ...request,
        })
        .promise()
        .then((response) => {
          this.groupPaginationToken = response.NextToken || "";
          return {
            nextToken: this.groupPaginationToken,
            group: request.GroupName,
            users: (response.Users || [])
              .map((u) => u.Username || "")
              .filter((o) => o),
          };
        })
        .catch(() => ({ nextToken: "", group: "", users: [] }));
    },
    async listGroupNames(): Promise<string[]> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .listGroups(this.request)
        .promise()
        .then((res) =>
          (res.Groups || []).map((g) => g.GroupName || "").filter((o) => o)
        );
    },
    getGroupsForUser(username: string, userGroups: IUserGroup[]): string[] {
      return userGroups
        .filter((g) => g.users.includes(username))
        .map((g) => g.group);
    },
    async listUsers(): Promise<User[]> {
      // Clean state
      this.groups = [];
      this.userGroups = [];
      this.users = [];
      // Get details from Cognito
      this.groups = await this.listGroupNames();
      for await (const usersInGroup of this.groups.map((groupName: string) =>
        this.cognitoListUsersInGroup(groupName)
      )) {
        this.userGroups.push(usersInGroup);
      }
      await this.cognitoListUsers();
      return this.users as User[];
    },
    async cognitoListUsersInGroup(groupName: string): Promise<IUserGroup> {
      const usersInGroup: IUserGroup = { group: groupName, users: [] };
      await this.getUserGroup({
        ...this.request,
        ...(this.groupPaginationToken && {
          NextToken: this.groupPaginationToken,
        }),
        GroupName: groupName,
        Limit: 60,
      }).then((r: any) => {
        usersInGroup.users = r.users;
        usersInGroup.nextToken = r.nextToken;
      });
      if (usersInGroup.nextToken) {
        const nextUsers = await this.cognitoListUsersInGroup(groupName);
        usersInGroup.users = usersInGroup.users.concat(nextUsers.users);
      }
      return usersInGroup;
    },
    async cognitoListUsers(): Promise<User[]> {
      let users: User[] = [];

      await (this.provider as CognitoIdentityServiceProvider)
        .listUsers({
          ...this.request,
          ...(this.paginationToken && {
            PaginationToken: this.paginationToken,
          }),
        })
        .promise()
        .then((response) => {
          this.paginationToken = response.PaginationToken || "";
          if (response.Users?.length) {
            users = response.Users.map((user) => {
              const organisationName =
                user.Attributes?.find(
                  ({ Name }) => Name === "custom:organisation"
                )?.Value || "";
              const coverHolder =
                user.Attributes?.find(
                  ({ Name }) => Name === "custom:cover_holder"
                )?.Value || "";
              return new User({
                username: user.Attributes
                  ? (user.Attributes.filter(({ Name }) => Name === "email")[0]
                      ?.Value as string)
                  : "",
                email: user.Attributes
                  ? (user.Attributes.filter(({ Name }) => Name === "email")[0]
                      ?.Value as string)
                  : "",
                role: "",
                groups: this.getGroupsForUser(
                  user.Username || "",
                  this.userGroups
                ),
                verifiedEmail:
                  user.Attributes?.filter(
                    ({ Name }) => Name === "email_verified"
                  )[0]?.Value || "",
                organisationName,
                coverHolder,
                createdAt: user.UserCreateDate?.toISOString(),
                updatedAt: user.UserLastModifiedDate?.toISOString(),
              });
            });
          }
        })
        .catch((err: any) => {
          console.log(err);
        });

      // If users exists, add results from paginated request
      this.users = sortBy([...this.users, ...users], (u) => u.username);

      // Recursive call to list users with token applied to request
      if (this.paginationToken) {
        await this.cognitoListUsers();
      }

      return this.users as User[];
    },
    async getUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminGetUserRequest>
    ): Promise<any> {
      const userGroups: any[] =
        (await this.provider
          ?.adminListGroupsForUser({
            ...this.request,
            ...request,
          } as CognitoIdentityServiceProvider.Types.AdminGetUserRequest)
          .promise()
          .then((response: any) => {
            return response.Groups?.map(
              ({ GroupName }: any) => GroupName
            ) as string[];
          })
          .catch(() => {
            return [] as string[];
          })) || [];

      await (this.provider as CognitoIdentityServiceProvider)
        .adminGetUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminGetUserRequest)
        .promise()
        .then((response) => {
          const organisationName =
            response.UserAttributes?.find(
              ({ Name }) => Name === "custom:organisation"
            )?.Value || "";
          const coverHolder =
            response.UserAttributes?.find(
              ({ Name }) => Name === "custom:cover_holder"
            )?.Value || "";
          const user = new User({
            username: response.UserAttributes
              ? (response.UserAttributes.filter(
                  ({ Name }) => Name === "email"
                )[0].Value as string)
              : "",
            email: response.UserAttributes
              ? (response.UserAttributes.filter(
                  ({ Name }) => Name === "email"
                )[0].Value as string)
              : "",
            role: "",
            groups: userGroups,
            verifiedEmail:
              response.UserAttributes?.filter(
                ({ Name }) => Name === "email_verified"
              )[0].Value || "",
            organisationName,
            coverHolder,
            createdAt: response.UserCreateDate?.toISOString(),
            updatedAt: response.UserLastModifiedDate?.toISOString(),
          });

          if (this.users) {
            const index = this.users.findIndex(
              ({ username }: any) => username === request.Username
            );
            if (index > -1) {
              this.users.splice(index, 1, user);
            }
          }
        });
    },
    async updateUserAttributes(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminUpdateUserAttributes({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesRequest)
        .promise()
        .then((r) => {
          this.getUser({ Username: request.Username });
          return r;
        });
    },
    async getCurrentUser(): Promise<string> {
      return this.amplify.Auth.currentSession().then(
        (session: CognitoUserSession) => {
          this.currentUser = session.getAccessToken().payload.username;
        }
      );
    },
    async getCurrentUserGroups(): Promise<string[]> {
      await this.getProvider();
      const userCredentials = await this.currentAuthenticatedUser();
      this.currentUserGroups =
        userCredentials.signInUserSession.accessToken.payload["cognito:groups"];
      return this.currentUserGroups;
    },
    async addUserToGroup(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminAddUserToGroupRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminAddUserToGroup({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminAddUserToGroupRequest)
        .promise();
    },
    async removeUserFromGroup(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminRemoveUserFromGroupRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminRemoveUserFromGroup({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminRemoveUserFromGroupRequest)
        .promise();
    },
    async deleteUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminDeleteUserRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminDeleteUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminDeleteUserRequest)
        .promise()
        .then(() =>
          this.users.splice(
            this.users.findIndex(
              ({ username }: any) => username === request.Username
            ),
            1
          )
        );
    },
    async resetPassword(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminResetUserPasswordRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminResetUserPasswordResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminResetUserPassword({
          ...this.request,
          ...request,
          ClientMetadata: {
            Permanent: "--no-permanent",
          },
        } as CognitoIdentityServiceProvider.Types.AdminResetUserPasswordRequest)
        .promise();
    },
  },
});
